import { useApplyCouponMutation } from '@/src/api/rtkQuery/couponApi'
import { useGetItineraryDataQuery, useUpdateFareSelectionMutation } from '@/src/api/rtkQuery/itineraryApi'
import useClevertapForItinerary from '@/src/hooks/analytics/clevertap/itinerary'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import { useThemedToast } from '@/src/hooks/ui/useThemedToast'
import { Fare } from '@/src/types/itinerary'
import { getFareBrandDetails } from '@/src/utils/itinerary/fareFamily'
import { selectFareFamilyPayload } from '@/src/utils/itinerary/updatePayloadHelper'
import { useTranslations } from 'next-intl'

const useFareCardSelection = () => {
    const [updateFareSelection, { isLoading }] = useUpdateFareSelectionMutation()
    const { triggerItineraryEvent } = useClevertapForItinerary()
    const { showError } = useThemedToast()
    const [applyCoupon] = useApplyCouponMutation()
    const itineraryId = useGetItineraryId()
    const t = useTranslations('fareFamily')
    const {
        appliedCouponCode,
        faresList: faresToDisplay,
        fareFamilyEnabledOnReview
    } = useGetItineraryDataQuery(
        { itineraryId },
        {
            selectFromResult: ({ data }) => ({
                appliedCouponCode: data?.itineraryData?.couponDetails?.[0]?.couponCode || '',
                faresList: data?.fareFamilyFares || [],
                fareFamilyEnabledOnReview: data?.meta?.fareFamilyEnabledOnReview
            })
        }
    )

    const title = t('fareFamilyTitle')
    const plansTitle = t('Plans')

    const reapplyCouponSilently = async () => {
        if (!appliedCouponCode) return

        applyCoupon({
            itineraryId,
            payload: { couponCodes: [appliedCouponCode], validatePayments: false }
        })
            .unwrap()
            .catch(error => {
                console.log('Coupon could not be reapplied after fare change:', error)
            })
    }

    const triggerFareFamilyFareClickEvent = (fare: Fare) => {
        const previousSelection = faresToDisplay.find(fare => fare.selected)
        const { ctBrandName: previousCtBrandName, fareTitle: previousFareTitle } = getFareBrandDetails(
            previousSelection as Fare
        )
        const { ctBrandName: currentCtBrandName, fareTitle: currentFareTitle } = getFareBrandDetails(fare)
        triggerItineraryEvent('Air_UI_Action', {
            action_name: 'fare_family_fare_clicked',
            action_type: 'BUTTON',
            previous_fare_brand: previousCtBrandName,
            previous_fare_title: previousFareTitle,
            current_fare_brand: currentCtBrandName,
            current_fare_title: currentFareTitle
        })
    }

    const onFareClick = async (fare: Fare) => {
        const { selected } = fare || {}
        if (selected || isLoading) {
            return // Don't do anything if already selected or loading
        }

        try {
            const payload = selectFareFamilyPayload({
                itineraryId,
                selectedFare: fare
            })
            triggerFareFamilyFareClickEvent(fare)

            await updateFareSelection({
                payload,
                itineraryId,
                selectedFare: fare
            }).unwrap()

            // After successful fare selection, silently reapply any applied coupon
            reapplyCouponSilently()

            // showSuccess('Fare selection updated successfully')
        } catch (error: any) {
            showError(error?.data?.message || 'Failed to update fare selection. Please try again.')
        }
    }

    return {
        title,
        plansTitle,
        fareFamilyEnabledOnReview,
        faresToDisplay,
        onFareClick
    }
}

export default useFareCardSelection
